/**
 * /Recon portal dashboard — list of the client's engagements.
 *
 * The first thing a client sees after logging in. Each row links to
 * the engagement detail page. Empty state guides newly-signed-up
 * clients who have just one engagement: "your assessment is being
 * prepared, we'll email you when deliverables are ready."
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getDashboardPulseApiV1ClientPortalPulseGet,
  listEngagementsApiV1ClientPortalEngagementsGet,
} from "@hackereyes/api-client";
import "./dashboard.css";

/* --- Dashboard pulse (Bucket QP-W3) ---------------------------------- */
interface PulseNextAction {
  evidence_card_id: string;
  title: string;
  severity: string;
  case_id: string;
  case_number: string;
  current_state: string;
  is_kev: boolean;
}

interface PulseStats {
  todo: number;
  acknowledged: number;
  in_progress: number;
  fixed: number;
  accepted_risk: number;
  needs_help: number;
}

interface PulseSparkPoint {
  date: string;
  open: number;
}

interface DashboardPulse {
  next_action: PulseNextAction | null;
  stats: PulseStats;
  sparkline: PulseSparkPoint[];
}

interface EngagementTarget {
  primary_identifier: string;
  display_name: string;
  target_type: string;
}

interface Engagement {
  id: string;
  case_number: string;
  title: string;
  status: string;
  case_type: string;
  payment_status: string | null;
  engagement_letter_signed_at: string | null;
  created_at: string;
  opened_at: string | null;
  closed_at: string | null;
  in_scope_targets: EngagementTarget[];
  in_scope_target_count: number;
}

const STATUS_COPY: Record<string, { label: string; tone: string }> = {
  // Pattern B (Bucket M): authorization happens at checkout — no
  // separate magic-link signing step. A paid case in `intake` is in
  // the brief window between webhook materialization and the
  // operator starting the scan. Surface that as "Authorized" rather
  // than the misleading legacy "Awaiting authorization."
  intake: { label: "Authorized", tone: "cyan" },
  pending: { label: "Awaiting payment", tone: "amber" },
  active: { label: "In progress", tone: "cyan" },
  review: { label: "Pending review", tone: "cobalt" },
  delivered: { label: "Delivered", tone: "green" },
  closed: { label: "Closed", tone: "slate" },
};

export function PortalDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["client-engagements"],
    queryFn: async () => {
      const { data, error } = await listEngagementsApiV1ClientPortalEngagementsGet();
      if (error) {
        throw new Error(((error as Record<string, unknown>)?.detail as string) || "load_failed");
      }
      // Filter out engagements that never completed payment. Pattern B
      // (Bucket M) prevents these from being created in the first place
      // by deferring case materialization to the Stripe webhook, but any
      // pre-Pattern-B Pattern A rows with payment_status='cancelled' are
      // hidden here so the dashboard isn't cluttered with abandonment
      // detritus. Refunded engagements stay visible because they were
      // real engagements at one point.
      const rows = (data ?? []) as unknown as Engagement[];
      return rows.filter((r) => r.payment_status !== "cancelled");
    },
  });

  const { data: pulse } = useQuery({
    queryKey: ["client-pulse"],
    queryFn: async () => {
      const { data, error } = await getDashboardPulseApiV1ClientPortalPulseGet();
      if (error) {
        throw new Error("pulse_load_failed");
      }
      return data as unknown as DashboardPulse;
    },
  });

  return (
    <div className="dashboard">
      <div className="dashboard-inner">
        <div className="dashboard-header">
          <div className="dashboard-header-row">
            <div className="dashboard-header-text">
              <div className="eyebrow">Your engagements</div>
              <h1 className="dashboard-title">Engagements</h1>
              <p className="dashboard-lede">
                Active and historical engagements with SignumCyber. Open one to see
                scope, status, deliverables, and any documents you’ve exchanged
                with your assessor.
              </p>
            </div>
            {data && data.length > 0 && (
              <Link
                to="/portal/commission"
                className="recon-btn recon-btn-primary dashboard-commission-cta"
              >
                + Commission new engagement
              </Link>
            )}
          </div>
        </div>

        {isLoading && <DashboardSkeleton />}

        {error && (
          <div className="dashboard-error">
            We couldn’t load your engagements. Try refreshing, or contact your
            assessor if the problem persists.
          </div>
        )}

        {data && data.length === 0 && (
          <div className="dashboard-welcome">
            <h2 className="dashboard-welcome-title">Commission your first assessment.</h2>
            <p className="dashboard-welcome-body">
              /Recon assessments map what an attacker would discover about
              your business. Configure the scope, authorize the scan at
              checkout, and the assessment runs in 3–5 business days.
              Deliverables appear here when they’re ready.
            </p>
            <Link to="/portal/commission" className="recon-btn recon-btn-primary recon-btn-lg">
              Commission an assessment →
            </Link>
            <p className="dashboard-welcome-hint">
              Check your email for a verification link if you haven’t already.
            </p>
          </div>
        )}

        {data && data.length > 0 && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-title">
              Your engagement{data.length === 1 ? "" : "s"}
            </h2>
            <div className="engagement-list">
              {data.map((eng) => (
                <EngagementRow key={eng.id} engagement={eng} />
              ))}
            </div>
          </section>
        )}

        {/* Pulse strip moved below the engagement list. When a buyer
         *  lands on the dashboard with a freshly delivered engagement,
         *  the first thing they should see is the engagement itself
         *  ("your report is ready") — not a single critical finding
         *  card pushing them into the action tracker before they've
         *  read the report it came from. */}
        {data && data.length > 0 && pulse && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-title">
              Across your engagements
            </h2>
            <PulseStrip pulse={pulse} />
          </section>
        )}
      </div>
    </div>
  );
}

function summarizeAssets(targets: EngagementTarget[]): string {
  const domains = targets.filter((t) => t.target_type === "domain");
  if (domains.length === 0) {
    const first = targets[0];
    return first ? first.display_name || first.primary_identifier : "no assets";
  }
  if (domains.length === 1) {
    const d = domains[0]!;
    return d.display_name || d.primary_identifier;
  }
  const head = domains[0]!.display_name || domains[0]!.primary_identifier;
  return `${head} + ${domains.length - 1} more`;
}

function EngagementRow({ engagement }: { engagement: Engagement }) {
  const status = STATUS_COPY[engagement.status] || {
    label: engagement.status,
    tone: "slate",
  };
  const created = new Date(engagement.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const assets = engagement.in_scope_targets || [];
  const assetSummary = summarizeAssets(assets);
  // Delivered engagements get a visual lift so they stand out in the
  // list as "this one needs your attention next." The buyer probably
  // got an email; they're on this page to find the report; we should
  // make finding it effortless.
  const isReportReady = engagement.status === "delivered";

  return (
    <Link
      to={`/portal/engagements/${engagement.id}`}
      className={`engagement-row${isReportReady ? " engagement-row-ready" : ""}`}
    >
      <div className="engagement-row-left">
        <div className="engagement-ref">{engagement.case_number}</div>
        <div className="engagement-title">{assetSummary}</div>
        <div className="engagement-meta">
          {engagement.case_type.replace(/_/g, " ")} ·{" "}
          {engagement.in_scope_target_count || assets.length} asset
          {(engagement.in_scope_target_count || assets.length) === 1 ? "" : "s"} ·
          Started {created}
        </div>
      </div>
      <div className="engagement-row-right">
        <span className={`engagement-status engagement-status-${status.tone}`}>
          {status.label}
        </span>
        {isReportReady ? (
          <span className="engagement-row-cta">Open report →</span>
        ) : (
          <span className="engagement-arrow" aria-hidden="true">→</span>
        )}
      </div>
    </Link>
  );
}

/* --- Pulse strip (Bucket QP-W3) -------------------------------------- */

const PULSE_SEV_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

function PulseStrip({ pulse }: { pulse: DashboardPulse }) {
  const { next_action, stats, sparkline } = pulse;

  const actionable =
    stats.todo + stats.acknowledged + stats.in_progress + stats.needs_help;

  return (
    <div className="pulse-strip">
      <div className="pulse-strip-grid">
        <PulseNextActionCard next={next_action} />
        <PulseStatsCard stats={stats} actionable={actionable} />
        <PulseSparklineCard data={sparkline} />
      </div>
    </div>
  );
}

function PulseNextActionCard({ next }: { next: PulseNextAction | null }) {
  if (!next) {
    return (
      <div className="pulse-card pulse-card-next pulse-card-empty">
        <div className="pulse-card-eyebrow">Your next action</div>
        <div className="pulse-card-title">Nothing actionable</div>
        <p className="pulse-card-body">
          Every finding you've been emailed about is either fixed, accepted,
          or awaiting an assessor reply. Refresh after your next scan to
          see new items here.
        </p>
      </div>
    );
  }

  const sev = (next.severity || "low").toLowerCase();
  return (
    <Link
      to={`/portal/engagements/${next.case_id}#action-${next.evidence_card_id}`}
      className="pulse-card pulse-card-next"
    >
      <div className="pulse-card-eyebrow">
        Your next action
        {next.is_kev && (
          <span className="pulse-card-kev" title="CISA Known Exploited Vulnerability">
            KEV
          </span>
        )}
      </div>
      <div className="pulse-card-title">{next.title}</div>
      <div className="pulse-card-meta">
        <span className={`pulse-card-sev pulse-card-sev-${sev}`}>
          {PULSE_SEV_LABEL[sev] ?? sev}
        </span>
        <span className="muted">·</span>
        <span className="pulse-card-case">{next.case_number}</span>
      </div>
      <span className="pulse-card-cta">Open in Action Tracker →</span>
    </Link>
  );
}

function PulseStatsCard({
  stats,
  actionable,
}: {
  stats: PulseStats;
  actionable: number;
}) {
  return (
    <div className="pulse-card pulse-card-stats">
      <div className="pulse-card-eyebrow">Pulse</div>
      <div className="pulse-card-title">
        {actionable} item{actionable === 1 ? "" : "s"} on your plate
      </div>
      <div className="pulse-stat-grid">
        <PulseStat label="To do" value={stats.todo + stats.acknowledged} tone="slate" />
        <PulseStat label="In progress" value={stats.in_progress} tone="cyan" />
        <PulseStat label="Fixed" value={stats.fixed} tone="green" />
        <PulseStat label="Accepted" value={stats.accepted_risk} tone="amber" />
        <PulseStat label="Needs help" value={stats.needs_help} tone="flame" />
      </div>
    </div>
  );
}

function PulseStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className={`pulse-stat pulse-stat-${tone}`}>
      <span className="pulse-stat-value">{value}</span>
      <span className="pulse-stat-label">{label}</span>
    </div>
  );
}

function PulseSparklineCard({ data }: { data: PulseSparkPoint[] }) {
  const points = data.length;
  // Guard against an all-zero series: max=0 would make the polyline
  // a flat line at the bottom, which is the correct visual but would
  // also produce a div-by-zero when normalizing. Clamp to 1 so the
  // line sits at the baseline cleanly.
  const max = Math.max(1, ...data.map((p) => p.open));

  // SVG dimensions: small inline chart, ~280×72px viewbox so we can
  // scale via CSS without losing the fixed aspect ratio.
  const W = 280;
  const H = 72;
  const stepX = points > 1 ? W / (points - 1) : 0;

  const polyline =
    points === 0
      ? // No data at all — render a single-point line at the baseline
        // so the SVG isn't visually empty.
        `0,${(H - 4).toFixed(1)} ${W},${(H - 4).toFixed(1)}`
      : data
          .map((p, i) => {
            const x = stepX * i;
            const y = H - (p.open / max) * H * 0.9 - 4;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");

  const latest = data[data.length - 1]?.open ?? 0;
  const earliest = data[0]?.open ?? 0;
  const delta = latest - earliest;

  return (
    <div className="pulse-card pulse-card-spark">
      <div className="pulse-card-eyebrow">90-day open findings</div>
      <div className="pulse-card-title">{latest} open today</div>
      {delta !== 0 && (
        <div
          className={`pulse-card-delta ${delta < 0 ? "is-down" : "is-up"}`}
        >
          {delta > 0 ? "+" : ""}
          {delta} vs 90 days ago
        </div>
      )}
      <svg
        className="pulse-sparkline"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Open findings over the last 90 days, latest ${latest}`}
      >
        <polyline points={polyline} fill="none" />
      </svg>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="engagement-list">
      {[0, 1, 2].map((i) => (
        <div key={i} className="engagement-row engagement-row-skeleton">
          <div className="engagement-row-left">
            <div className="skeleton-line skeleton-line-sm" />
            <div className="skeleton-line skeleton-line-md" />
            <div className="skeleton-line skeleton-line-xs" />
          </div>
        </div>
      ))}
    </div>
  );
}
